import React, { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as Logo } from '../../assets/icons/logo-12.svg'
import { useAuth } from '../../hooks'
import { Button } from '../../components/ui'
import { useRenewPlan } from '../../hooks/api/billingHooks'
import {
	isBlockedPlanStatus,
	isExpiredPlanStatus,
} from '../../context/billingContext'
import { getErrorData } from '../../lib/utils/handleApiError'
import { APP_BASENAME } from '../../lib/utils/authRedirect'
import ChoosePlanModal from '../../components/modals/user/ChoosePlanModal'

const PlanBlock: FC = () => {
	const { currentUser, logout } = useAuth()
	const navigate = useNavigate()
	const {
		mutateAsync: renewPlan,
		isPending: isRenewing,
		isError,
	} = useRenewPlan()

	const [renewError, setRenewError] = useState<string | null>(null)
	const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false)

	const isOwner =
		currentUser?.organization?.isOwner || !currentUser?.organization
	const isExpired = isExpiredPlanStatus(currentUser?.plan?.status)
	const isBlocked = isBlockedPlanStatus(currentUser?.plan?.status)

	useEffect(() => {
		if (currentUser?.id && !isExpired && !isBlocked) {
			navigate('/settings/user', { replace: true })
		}
	}, [currentUser?.id, isExpired, isBlocked, navigate])

	const handleSupport = () => {
		window.open(`mailto:support@solarion.ai`, '_blank')
	}

	if (currentUser?.id && !isExpired && !isBlocked) {
		return null
	}

	return (
		<>
			<div className="w-full h-screen overflow-hidden flex items-center justify-center flex-col">
				<div className="flex flex-col items-center justify-center">
					<Logo className="w-[31px] h-[44px]" />
					<div className="px-4 flex flex-col items-center justify-start mb-6">
						<h3 className="font-extrabold text-2xl bg-gradient-to-r dark:text-text-main-dark font-roboto">
							AURA
						</h3>
						<p className="font-normal text-[10px] text-text-secondary text-nowrap">
							Powered by <span className="font-bold">Solarion AI</span>
						</p>
					</div>
				</div>
				<div className="mb-8 px-10 text-center">
					<h1 className="text-3xl text-center mb-7">
						{isBlocked ? 'Account In Review' : 'Subscription Expired'}
					</h1>
					{isBlocked && isOwner ? (
						<p className="text-center">
							Your account is currently under review by{' '}
							<span className="font-semibold">Solarion Team</span>. We will
							communicate our decision of your account status via email. <br />{' '}
							If you did not receive an email within 24 hours, please contact
							<span
								className="font-medium text-accent hover:text-accent-hover cursor-pointer"
								onClick={handleSupport}
							>
								{' '}
								support@solarion.ai
							</span>
						</p>
					) : (
						<p>
							{isOwner
								? 'Your subscription has expired. Please renew the subscription to gain access to the application.'
								: 'The subscription for your organization has expired. Please contact your organization administrator to renew the subscription.'}
						</p>
					)}

					{isError && isExpired && (
						<div className="mt-5">
							<p className="text-center">
								If you have any issues, please contact
								<span
									className="font-medium text-accent hover:text-accent-hover cursor-pointer"
									onClick={handleSupport}
								>
									{' '}
									support@solarion.ai
								</span>
							</p>
						</div>
					)}
				</div>
				<div className="flex items-center justify-center gap-5">
					<Button
						className="w-40"
						variant="secondary"
						label="Back to Login"
						onClick={() => logout()}
					/>
					{!isBlocked && isOwner && (
						<>
							{[
								'BILLING_INVOICE_NOT_FOUND',
								'CURRENT_PLAN_NOT_AVAILABLE',
							].includes(renewError) ? (
								<Button
									className="w-40"
									label="Change plan"
									loading={isRenewing}
									onClick={() => setIsChangePlanModalOpen(true)}
								/>
							) : (
								<Button
									className="w-40"
									label="Renew"
									loading={isRenewing}
									onClick={async () => {
										let stripeUrl = ''
										let renewalSucceeded = false

										await renewPlan(undefined, {
											onSuccess: ({ data }) => {
												renewalSucceeded = true
												stripeUrl = data?.data?.url || ''
											},
											onError: (error) => {
												const { errorMessageCode } = getErrorData(error)
												setRenewError(errorMessageCode)
											},
										})

										if (stripeUrl) {
											window.open(stripeUrl, '_blank')
											return
										}
										if (renewalSucceeded) {
											window.location.replace(
												`${window.location.origin}${APP_BASENAME}/home`,
											)
										}
									}}
								/>
							)}
						</>
					)}
				</div>
			</div>

			<ChoosePlanModal
				isOpen={isChangePlanModalOpen}
				onClose={() => setIsChangePlanModalOpen(false)}
				currentUser={currentUser}
			/>
		</>
	)
}
export default PlanBlock
